import { Component, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
  AbstractControl,
} from '@angular/forms';
import {
  FieldConfig,
  ErrorMsgs,
  MobileNumber,
  fncValidateNoSpaces,
} from '../registration/registration.model';
import { PhoneNumberValidator } from '../validators/phone-number.validator';
import {
  config,
  getCountryConfig,
  getCountryConfig2,
} from '../config/country-config';
import { Subscription } from 'rxjs';
import { UtilsService } from '../services/utils.service';
import { AppCountry } from '../app.models';

export interface mobileCountryObj {
  countryDialingCode: string;
  iso3Code: any;
  iso2Code: any;
  name: string;
}
@Component({
    selector: 'app-phone-number',
    templateUrl: './phone-number.component.html',
    styleUrls: ['./phone-number.component.scss'],
    standalone: false
})
export class PhoneNumberComponent implements OnInit, OnDestroy {
  public _phoneFieldConfig!: FieldConfig;
  public countries: any[];
  static invalidPhoneNumber = 'invalidPhoneNumber';
  static invalid = 'INVALID';
  availableCountries = [
    'AR',
    'CZ',
    'HU',
    'MA',
    'MD',
    'PH',
    'PL',
    'RO',
    'SK',
    'UA',
    'US',
  ];

  // These countries should not be display in the phone country code dropdown (MGBC-303)
  private countriesNotToDisplay: string[] = ['HMD', 'ANT', 'YUG', 'SCG'];

  private _labels!: any;
  @Input() set labels(value: any) {
    this._labels = value;
  }

  public get labels() {
    return this._labels;
  }
  @Input() public set phoneFieldConfig(phoneConfig: FieldConfig) {
    this._phoneFieldConfig = phoneConfig;
  }
  public get phoneFieldConfig() {
    return this._phoneFieldConfig;
  }
  public _countryFieldConfig!: FieldConfig;
  @Input() public set countryFieldConfig(countryConfig: FieldConfig) {
    if (this.form) {
      this.handleDefaultValues(countryConfig);
    }
    this._countryFieldConfig = countryConfig;
  }
  public get countryFieldConfig() {
    return this._countryFieldConfig;
  }
  private _transactionCountryCode!: string;
  @Input() public set transactionCountryCode(code: string) {
    this._transactionCountryCode = code;

    if (this.form?.get('countryCode')) {
      this.onChange();
    }
  }
  public get transactionCountryCode(): string {
    return this._transactionCountryCode;
  }
  public countryDialingCode!: string;
  public mobileNumber!: string;

  private _phoneData!: MobileNumber;
  @Input() public set phoneData(data: MobileNumber) {
    if (data) {
      this._phoneData = data;
      if (this.phoneData) {
        const defaultCountry = this.countries.find(
          (c) =>
            c.countryDialingCode?.toString() ===
            this._phoneData.countryDialCode?.toString()
        );
        if (defaultCountry) {
          this.form.get('countryCode')?.setValue(defaultCountry?.iso2Code);
          this.form
            .get('phoneNumber')
            ?.setValue(
              this.utilsService.formatPhoneNumber(
                this.phoneData.number,
                defaultCountry?.iso2Code
              )
            );
        }
      }
    }
  }
  public get phoneData(): MobileNumber {
    return this._phoneData;
  }

  private _countryDialingCode?: string;

  @Input() parentForm = new FormGroup({});
  @Input() locale: string;

  get validationError(): ValidationErrors | null {
    return this.form.controls['phoneNumber'].hasError(
      PhoneNumberValidator.phoneNumber
    )
      ? { [PhoneNumberComponent.invalidPhoneNumber]: true }
      : null;
  }

  public get selectedFlag() {
    const countryControl = this.form.get('countryCode');

    // return countryControl?.value && `flag_${this.getCountryCode(getCountryConfig2(countryControl.value).iso2Code.toLowerCase())}` || undefined
    const iso2Code = this.selectedCountry?.iso2Code?.toLowerCase();
    return iso2Code ? `flag_${iso2Code}` : undefined;
  }

  public form!: FormGroup;
  // public readonly countries = this.filterUnsupportedCountries(config);
  public readonly errorMsgs = ErrorMsgs;
  public selectedCountry?: AppCountry;

  private onValueChange$: Subscription | undefined;

  constructor(private fb: FormBuilder, private utilsService: UtilsService) {}

  ngOnInit(): void {
    this.countries = this.filterUnsupportedCountries(config);
    if (
      this.locale &&
      this.locale.substring(0, 2) !== 'en' &&
      !!this._labels?.stellar?.country
    ) {
      this.countries = this.mapCountries(this.countries);
    }
    this.form = this.fb.group(
      {},
      {
        validator: PhoneNumberValidator.validatePhoneNumber(
          'countryCode',
          'phoneNumber'
        ),
      }
    );
    this.initForm();
  }

  private initForm() {
    const validators: ValidatorFn[] = [];
    if (this._phoneFieldConfig.required) {
      validators.push(Validators.required);
      validators.push(fncValidateNoSpaces);
    }

    let countryInitialValue;
    if (this._transactionCountryCode) {
      countryInitialValue = getCountryConfig(
        this._transactionCountryCode
      ).iso2Code;
    }

    this.form.addControl(
      'countryCode',
      this.fb.control(countryInitialValue, {})
    );
    this.form.addControl('countryDialCode', this.fb.control('', {}));
    this.form.addControl(
      'phoneNumber',
      this.fb.control('', {
        validators: [...validators],
      })
    );

    this.parentForm.addControl('phone', this.form);
    this.handleDefaultValues(this.selectedCountry!);

    if (this.form) {
      this.onValueChange$ = this.parentForm
        .get('phone')!
        .valueChanges.subscribe((event) => {
          this.onChange(event);
        });
      this.onValueChange$ = this.form
        .get('countryCode')!
        .valueChanges.subscribe((event) => {
          this.onChange(event);
        });
    }
  }

  private mapCountries(countries: mobileCountryObj[]): mobileCountryObj[] {
    return countries.reduce((countries1: mobileCountryObj[], country) => {
      if (this._labels?.stellar?.country[country.iso3Code]) {
        countries1.push({
          ...country,
          name:
            this._labels?.stellar?.country[country.iso3Code]?.name ||
            country.name,
        });
      }
      return countries1;
    }, []);
  }

  // Remove the countries that should not display in the phone country code dropdown
  private filterUnsupportedCountries(
    countries: AppCountry[]
  ): mobileCountryObj[] {
    return countries.reduce((countries1: mobileCountryObj[], country) => {
      if (!this.countriesNotToDisplay.includes(country.code)) {
        countries1.push({
          countryDialingCode: country.countryDialingCode,
          iso2Code: country.iso2Code,
          iso3Code: country.code,
          name: country.name,
        });
      }
      return countries1;
    }, []);
  }

  private handleDefaultValues(_country: FieldConfig | string): void {
    this.selectedCountry =
      (this._transactionCountryCode &&
        getCountryConfig(this._transactionCountryCode)) ||
      undefined;

    if (this.form && this.selectedCountry) {
      this.form.get('countryCode')!.setValue(this.selectedCountry!.iso2Code);
      this.form
        .get('countryDialCode')
        ?.setValue(this.selectedCountry!.countryDialingCode);
    }
  }

  protected onChange(event?: any): void {
    const countryControl = this.form.get('countryCode')!;
    const numberControl = this.form.get('phoneNumber')!;
    const countryDialCodeCtrl = this.form.get('countryDialCode')!;

    // transactionCountry passed as iso3 `code` property on `AppCountry`
    // control value setup to track/use `iso2Code`
    this.selectedCountry =
      (countryControl.value && getCountryConfig2(countryControl.value)) ||
      (this._transactionCountryCode &&
        getCountryConfig(this._transactionCountryCode)) ||
      undefined;
    if (
      this.selectedCountry &&
      countryDialCodeCtrl &&
      countryDialCodeCtrl.value !== this.selectedCountry.countryDialingCode &&
      countryControl.value !== this.selectedCountry.iso2Code
    ) {
      countryDialCodeCtrl.setValue(this.selectedCountry.countryDialingCode);
      countryControl.setValue(this.selectedCountry.iso2Code);
    } else if (
      countryDialCodeCtrl &&
      countryDialCodeCtrl.value !== this.selectedCountry?.countryDialingCode
    ) {
      countryDialCodeCtrl.setValue(this.selectedCountry?.countryDialingCode);
    }

    if (this.validationError === null) {
      if (numberControl.hasError('phoneNumber')) {
        numberControl.setErrors({
          ...numberControl.errors,
          phoneNumber: undefined,
        });
      }

      try {
        const formattedNumber = this.utilsService.formatPhoneNumber(
          numberControl.value,
          this.selectedCountry?.iso2Code
        );

        if (numberControl.value !== formattedNumber) {
          numberControl.setValue(formattedNumber);
        }
      } catch (e) {}
    }
  }

  public getCountryCode(iso2Code: string): string {
    const isAvailable = this.availableCountries.some(
      (code) => code === iso2Code
    );
    return isAvailable ? iso2Code.toLowerCase() : 'us'; // Only 11 country flags are available, if any others return default value as 'US'
  }

  public getCountryDialingCode(): string {
    let countryDialingCode = '';
    const selectedCountryCode = this.form.get('countryCode')?.value;
    if (selectedCountryCode) {
      //return getCountryConfig(this._transactionCountry).countryDialingCode
      const selectedCountryIndex = this.countries?.findIndex(
        (country: any) =>
          country.iso2Code.toLowerCase() === selectedCountryCode.toLowerCase()
      );
      if (selectedCountryIndex >= 0) {
        countryDialingCode =
          this.countries[selectedCountryIndex].countryDialingCode;
      }
    }
    return countryDialingCode;
  }

  public ngOnDestroy(): void {
    if (this.onValueChange$) {
      this.onValueChange$.unsubscribe();
    }
  }
}
